import React from 'react';
import { Grid, Box, Typography, Chip, Paper, Tooltip, IconButton } from '@mui/material';
import { TrendingUp, TrendingDown, AttachMoney, Leaderboard, Calculate, Info } from '@mui/icons-material';

const formatCurrency = (value) => {
  if (typeof value !== 'number' || isNaN(value)) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatPercent = (value) => {
  if (typeof value !== 'number' || isNaN(value)) return '0%';
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
};

const KpiCard = ({ titulo, valor, icon: Icon, cor, descricao }) => (
  <Paper elevation={1} sx={{ p: 2, height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {Icon && <Icon sx={{ fontSize: 20, color: `${cor}.main`, mr: 0.5 }} />}
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
          {titulo}
        </Typography>
      </Box>
      <Tooltip title={descricao} arrow placement="top">
        <IconButton size="small" sx={{ p: 0.25, ml: 0.5 }}>
          <Info sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'baseline', flexGrow: 1 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
        {valor}
      </Typography>
    </Box>
  </Paper>
);

const FinanceiroCards = ({ dataContrato = [], comparativoTrimestral = null }) => {
  if (!dataContrato.length) return null;

  const totalPrevisto = dataContrato.reduce((acc, item) => acc + (item.previsto || 0), 0);
  const totalRealizado = dataContrato.reduce((acc, item) => acc + (item.realizado || 0), 0);
  const diferencaAbsoluta = totalRealizado - totalPrevisto;
  const producaoAcimaMeta = dataContrato
    .filter(item => item.realizado > item.previsto)
    .reduce((acc, item) => acc + (item.realizado - item.previsto), 0);
  const desvioFaturamento = totalPrevisto > 0 ? ((totalRealizado / totalPrevisto) - 1) * 100 : 0;

  const ranking = [...dataContrato]
    .map(item => ({
      contrato: item.contrato,
      percentual: item.previsto > 0 ? ((item.realizado / item.previsto) - 1) * 100 : -100,
    }))
    .sort((a, b) => b.percentual - a.percentual);

  const comparativoPercentual = comparativoTrimestral?.variacao_percentual ?? null;
  const comparativoDescricao = comparativoTrimestral?.periodo_atual && comparativoTrimestral?.periodo_anterior
    ? `${comparativoTrimestral.periodo_atual} vs ${comparativoTrimestral.periodo_anterior}`
    : 'Últimos 3 meses vs 3 meses anteriores';

  const tituloDesvio = desvioFaturamento >= 0 ? 'Superavit' : 'Desvio %';
  const descricoes = {
    diferenca: 'Valor total realizado menos o valor previsto',
    acimaMeta: 'Soma dos valores que ultrapassaram a meta prevista do contrato',
    desvio: 'Percentual de ganho ou perda em relação à meta prevista total',
    comparativo: comparativoDescricao,
    ranking: 'Contrato com melhor desempenho percentual em relação à meta prevista',
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={4} md={3}>
          <KpiCard
            titulo="Diferença Absoluta"
            valor={formatCurrency(diferencaAbsoluta)}
            icon={Calculate}
            cor={diferencaAbsoluta >= 0 ? 'success' : 'error'}
            descricao={descricoes.diferenca}
          />
        </Grid>

        <Grid item xs={6} sm={4} md={3}>
          <KpiCard
            titulo="Acima da Meta"
            valor={formatCurrency(producaoAcimaMeta)}
            icon={TrendingUp}
            cor="success"
            descricao={descricoes.acimaMeta}
          />
        </Grid>

        <Grid item xs={6} sm={4} md={3}>
          <KpiCard
            titulo={tituloDesvio}
            valor={formatPercent(desvioFaturamento)}
            icon={AttachMoney}
            cor={desvioFaturamento >= 0 ? 'success' : 'error'}
            descricao={descricoes.desvio}
          />
        </Grid>

        <Grid item xs={6} sm={4} md={3}>
          <KpiCard
            titulo="Comparativo Trimestral"
            valor={comparativoPercentual !== null ? formatPercent(comparativoPercentual) : '--'}
            icon={comparativoPercentual !== null && comparativoPercentual >= 0 ? TrendingUp : TrendingDown}
            cor={comparativoPercentual !== null && comparativoPercentual >= 0 ? 'success' : comparativoPercentual !== null ? 'error' : 'default'}
            descricao={descricoes.comparativo}
          />
        </Grid>

        <Grid item xs={12} sm={8} md={9}>
          <KpiCard
            titulo="Ranking"
            valor={ranking[0]?.contrato || '--'}
            icon={Leaderboard}
            cor="info"
            descricao={descricoes.ranking}
          />
        </Grid>
      </Grid>

      {ranking.length > 0 && (
        <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {ranking.slice(0, 5).map((item, index) => (
            <Chip
              key={item.contrato}
              label={`${index + 1}. ${item.contrato.substring(0, 18)}${item.contrato.length > 18 ? '...' : ''}: ${formatPercent(item.percentual)}`}
              color={item.percentual >= 0 ? 'success' : 'error'}
              size="small"
              variant={index === 0 ? 'filled' : 'outlined'}
              sx={{ fontSize: '0.7rem', height: 24 }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default FinanceiroCards;